import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import styles from './Modal.module.css'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /**
   * Whether Escape closes the dialog.
   *
   * Set false where closing loses something unrecoverable — the one-time
   * mentor password is the case this exists for.
   *
   * Note this no longer governs backdrop clicks: those never close a dialog.
   * See the comment on `handleBackdropClick`.
   */
  dismissible?: boolean
}

/**
 * Modal dialog built on `<dialog>`.
 *
 * *** THE NATIVE ELEMENT DOES THE HARD PARTS ***
 * Focus trapping, inertness of the page behind, and top-layer stacking are all
 * handled by `showModal()`. A hand-rolled div implementation has to reimplement
 * every one of those, and usually gets focus return wrong.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  dismissible = true,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (open && !dialog.open) {
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    // Escape fires the native `cancel` event. Intercept it so React state stays
    // the source of truth, and so a non-dismissible dialog can refuse.
    const handleCancel = (event: Event) => {
      event.preventDefault()
      if (dismissible) onClose()
    }

    dialog.addEventListener('cancel', handleCancel)
    return () => dialog.removeEventListener('cancel', handleCancel)
  }, [dismissible, onClose])

  return (
    <dialog
      ref={dialogRef}
      className={`${styles.dialog} ${styles[size]}`}
      /*
       * *** A CLICK OUTSIDE MUST NOT DISCARD A HALF-FILLED FORM ***
       * These dialogs are forms, not lightboxes. Losing ten fields of typing
       * to a stray click on the backdrop is a data-loss bug wearing the
       * costume of a convenience feature, so dismissal is deliberate only:
       * the ×, Cancel, or Escape.
       *
       * The handler is gone rather than neutered — a no-op onClick would
       * still invite someone to "restore" it later without the reasoning.
       */
      aria-labelledby="modal-title"
      aria-describedby={description ? 'modal-description' : undefined}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <div>
            <h2 id="modal-title" className={styles.title}>
              {title}
            </h2>
            {description && (
              <p id="modal-description" className={styles.description}>
                {description}
              </p>
            )}
          </div>

          {dismissible && (
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          )}
        </header>

        <div className={styles.body}>{children}</div>

        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </dialog>
  )
}

interface ConfirmProps {
  open: boolean
  title: string
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  destructive?: boolean
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * Confirmation dialog for irreversible actions.
 *
 * `message` should state the CONSEQUENCE, not ask "are you sure" — someone who
 * has read "Are you sure?" a hundred times stops reading it.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmProps) {
  return (
    <Modal open={open} onClose={onCancel} title={title} size="sm">
      <p className={styles.confirmMessage}>{message}</p>

      <div className={styles.confirmActions}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={onCancel}
          disabled={loading}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={destructive ? styles.dangerButton : styles.primaryButton}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  )
}
